// ─── Fiche INFO d'un espace (lecture seule) ───────────────────────
// Répond à « qu'est-ce qu'il y a dans cet espace ? » : membres, rôle, et la
// LISTE de tout ce qu'il contient — calendriers, inventaires, plans… Le Core ne
// connaissant pas ces collections, l'app les déclare via `sources` (les mêmes
// que « Sélection des éléments visibles »), chaque élément portant son
// `espaceId`.
import React, { useMemo } from 'react';
import { View, Text, TouchableOpacity, StyleSheet } from 'react-native';
import Feuille from '../ui/Feuille';
import { useUI } from '../theme/PreferencesUI';
import { useT } from '../i18n/LangueContext';
import { useAuth } from '../auth/AuthContext';
import { roleDansEspace } from './espaces';
import CarteInfos from '../ui/CarteInfos';

const HIT = { top: 8, bottom: 8, left: 8, right: 8 };

export default function FicheEspace({ espace, sources = [], actif, onFermer, onRendreActif, onEditer }) {
  const UI = useUI();
  const t = useT();
  const s = useMemo(() => makeStyles(UI), [UI]);
  const { user } = useAuth();
  if (!espace) return null;

  const role = roleDansEspace(espace, user?.uid);
  const nbMembres = (espace.membreUids || []).length;
  // Ce que l'espace CONTIENT, par type — un espace vide se voit aussi.
  const groupes = sources.map(src => ({
    ...src,
    elements: (src.elements || []).filter(el => el.espaceId === espace.id),
  }));
  const total = groupes.reduce((n, g) => n + g.elements.length, 0);

  return (
    <Feuille visible={!!espace} titre={espace.nom || t('profil.espace')} emoji={espace.emoji || '🏠'} hauteur={0.7}
      onFermer={onFermer}
      action={onEditer ? { icone: '✏️', label: t('commun.modifier'), onPress: onEditer } : undefined}
      boutons={(!actif && onRendreActif) ? (
        <TouchableOpacity hitSlop={HIT} onPress={onRendreActif} accessibilityLabel={t('profil.rendreActif')}>
          <Text style={s.action}>★</Text>
        </TouchableOpacity>
      ) : undefined}>
      {/* MÊMES propriétés que la fiche d'un calendrier, dans le même ordre et
          avec le même vocabulaire : « Propriétaire : Moi », pas « Mon rôle ». */}
      <CarteInfos rows={[
        { ic: '👤', lbl: t('jetons.proprietaire'), val: role === 'proprietaire' ? t('jetons.moi') : t('jetons.autreMembre') },
        { ic: '👥', lbl: t('profil.membres'), val: String(nbMembres) },
        { ic: '📦', lbl: t('jetons.contenu'), val: t('jetons.nElements', { n: total }) },
        { ic: '★', lbl: t('calendrier.actif'), val: actif ? t('commun.oui') : t('commun.non') },
      ]} />

      {total === 0 && <Text style={s.vide}>{t('jetons.espaceVide')}</Text>}
      {groupes.filter(g => g.elements.length).map(g => (
        <View key={g.cle}>
          <Text style={s.groupe}>{g.emoji ? `${g.emoji} ` : ''}{g.label}</Text>
          {g.elements.map(el => (
            <View key={el.id} style={s.ligne}>
              <Text style={s.nom} numberOfLines={1}>{el.nom || el.id}</Text>
              {el.proprietaire === false && <Text style={s.sous}>{t('jetons.autreMembre')}</Text>}
            </View>
          ))}
        </View>
      ))}
    </Feuille>
  );
}

const makeStyles = (UI) => StyleSheet.create({
  action: { fontSize: 19, color: UI.accent, fontWeight: '800' },
  groupe: { fontSize: 12, fontWeight: '900', color: UI.muted, textTransform: 'uppercase',
            letterSpacing: 0.5, marginTop: 18, marginBottom: 4 },
  ligne: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 10,
           paddingVertical: 10, borderTopWidth: 0.5, borderTopColor: UI.border },
  nom: { flex: 1, fontSize: 14, fontWeight: '700', color: UI.ink },
  sous: { fontSize: 11.5, color: UI.faint },
  vide: { fontSize: 12.5, color: UI.faint, fontStyle: 'italic', marginTop: 14 },
});
